import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'typesafe-actions';
import LeafletMap from './LeafletMap';
import InfoBox from './InfoBox';
import ShipInfoContainer from './ShipInfoContainer';
import ImageBar from './ImageBar';
import ExploreDrawer from './ExploreDrawer';
import ImageGallery from './ImageGallery';
import LoadingOverlay from './LoadingOverlay';
import { AlertSnackbar } from './SimpleSnackbar';

import './defaults.css';
import './FullSizeImage.css';
import './Header.css';
import './Footer.css';
import './SplashScreen.css';

import { Display } from '../state/uiControl/uiControl.reducer';

import {
  toggleFullSize,
} from '../state/uiControl/uiControl.actions';

import { isProd } from '../util';
import EntryDrawer from './EntryDrawer';
import ShipDrawer from './ShipDrawer';
import MobileSelectors from './MobileSelectors';
import SpeciesBox from './SpeciesBox';
import BulletinBox from './BulletinBox';
import Login from './Login';
import ports from '../assets/data/dataset.json';
import Header from './Header';
import Footer from './Footer';
import { useIsMobile } from '../hooks';

//TODO: make the files marked as new mobile friendly,
//Remove the files not marked as new

const seenSplashLocalStorage: any = window.localStorage.getItem('hasSeenSplash');
const isFeed = process.env.REACT_APP_SHOW_FEEDS;

const AppView: React.FC<AppViewProps> = (props) => {
  const {
    alert,
    isLoading,
    isShowingImages,
    isEntryShowing,
    isShipShowing,
    isPortShowing,
    isSpeciesShowing,
    isBulletinShowing,
    // selectedVoyage,
    numberOfPortsTraveled
  } = props;

  const [seenSplash, setSeenSplash] = useState(seenSplashLocalStorage);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const isMobile = useIsMobile();

  const [showExplorePopup, setShowExplorePopup] = useState<boolean>(false);
  const [prevShowExplorePopup, setPrevShowExplorePopup] = useState<typeof showExplorePopup>(showExplorePopup);

  useEffect(() => {
    if (isShipShowing) {
      setPrevShowExplorePopup(showExplorePopup);
      setShowExplorePopup(false);
    } else {
      setPrevShowExplorePopup(false);
      setShowExplorePopup(prevShowExplorePopup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShipShowing]);

  useEffect(() => {
    if (alert) {
      setSnackbarOpen(true);
    }
  }, [alert]);

  return (
    <React.Fragment>
      {!isMobile && isEntryShowing && <InfoBox />}
      {!isMobile && isBulletinShowing && <BulletinBox />}

      {!isMobile && isSpeciesShowing && <SpeciesBox />}

      {(!isMobile || isShowingImages) && (isBulletinShowing || isEntryShowing || isPortShowing || isSpeciesShowing) && <ImageBar />}
      {!isMobile && isShipShowing && <ShipInfoContainer />}

      <ShipDrawer />
      {isEntryShowing && <EntryDrawer />}

      {alert?.message &&
        <AlertSnackbar
          message={alert.message}
          open={snackbarOpen}
          setOpen={setSnackbarOpen}
          severity={alert?.severity}
        />}
      <LoadingOverlay open={isLoading}/>
      <ImageGallery />

      <Header setShowExplorePopup={setShowExplorePopup} />

      <LeafletMap />
      {!isFeed && <Footer />}
      <Login />

      <ExploreDrawer
        details={<MobileSelectors />}
        expanded={showExplorePopup}
        onChange={() => setShowExplorePopup((show) => !show)}
        show={!isEntryShowing && (isMobile || showExplorePopup)}
        title="Explore Voyages"
        isMobile={isMobile}
      />

      {!isProd && !isMobile && numberOfPortsTraveled !== undefined && <div className="legend">
        You've been to {numberOfPortsTraveled} out of {ports.length} Available Ports!
      </div>}

      {!seenSplash && !isMobile &&
        <div className="clickOff" onClick={() => {
          window.localStorage.setItem('hasSeenSplash', "true");
          setSeenSplash(true);
        }}>
          <video className="splashVideo" autoPlay muted loop>
            <source src='./Expeditions.mp4' type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="splashText">
            <div className="splashHeader">
              Seabourn Voyage Tracker
            </div>

            <div className="text">
              {/* Whether you are looking for an expansive globe-spanning journey or an intensive exploration of the breathtaking wonders of South America, start planning your adventure today.  */}
              From this page you can see the current location of the seabourn fleet, and by clicking on "Our Ships" in the top left you can view our incridible itineraries, browse blog entries, see trip photos and view or download voyage videos. Click anywhere on the screen to get started.
            </div>
          </div>
        </div>}
    </React.Fragment>
  );
};

const mapStateToProps = (state: RootState) => ({
  alert: state.uiControl.alert,
  selectedImage: state.uiControl.selectedImage,
  isShowingImages: state.uiControl.isShowingImages,
  isEntryShowing: state.uiControl.showing === Display.ENTRY,
  isShipShowing: state.uiControl.showing === Display.SHIP,
  isLoading: [state.voyagesData.isPositionDataLoading, state.voyagesData.isShipDataLoading].includes(true),
  isPortShowing: state.uiControl.showing === Display.PORT,
  isSpeciesShowing: state.uiControl.showing === Display.SPECIES,
  isBulletinShowing: state.uiControl.showing === Display.BULLETIN,
  selectedVoyage: state.voyagesData.selectedVoyageData,
  selectedYear: state.voyagesData.selectedYear,
  userData: state.user.userData,
  numberOfPortsTraveled: state.user.numberOfPortsTraveled
});

const dispatchProps = {
  toggleFullSize,
};

type AppViewProps = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

export default connect(
  mapStateToProps,
  dispatchProps
)(AppView);